import { useState } from "react";
import type { ProjectView } from "@joogoos/contracts";
import { Photos } from "./stages/Photos.js";
import { Voices } from "./stages/Voices.js";
import { Plan } from "./stages/Plan.js";
import { Pipeline } from "./stages/Pipeline.js";
import { Editor } from "./stages/Editor.js";
import { Icon } from "../ui/icons.js";
import { PublishDialog, publishableOf } from "./Publish.js";

/**
 * Zone 3: ideas to start from, then the room a choice needs, and after that the room the work needs.
 *
 * It is always there on a desk (changed 2026-09-12, docs/03). At the start it offers the gallery, beside the chat, as
 * the other way in. It changes when a question has to be *looked* at rather than answered in a word (which photos,
 * which voice), stays to say what is about to run and what it costs, and then becomes the pipeline and the editor,
 * which never leave for the life of the project.
 */
export type Stage = "ideas" | "photos" | "voices" | "plan" | "pipeline" | "editor";

export function stageOf(project: ProjectView | null, view: "pipeline" | "editor" | null): Stage {
  if (!project) return "ideas";
  if (project.status !== "chatting") {
    const rendered = project.renders.some((r) => r.status === "succeeded" && r.output);
    return view ?? (rendered ? "editor" : "pipeline");
  }
  const asking = project.nextQuestion?.field;
  if (asking === "sourceMediaIds") return "photos";
  if (asking === "voice") return "voices";
  if (project.draft.idea && project.missing.length === 0) return "plan";
  return "ideas";
}

export function Result({ project, stage, onView, onPicked }: {
  project: ProjectView;
  stage: Stage;
  onView: (v: "pipeline" | "editor") => void;
  /** The photo picker has been answered: the Studio closes it, if it opened it. */
  onPicked?: (() => void) | undefined;
}) {
  const rendered = project.renders.some((r) => r.status === "succeeded" && r.output);
  const started = project.status !== "chatting";
  // Publishing always opens the dialog and always ends in a person pressing it (ADR 0011, 24).
  const [publishing, setPublishing] = useState(false);

  return (
    <div className="h-full flex flex-col min-h-0">
      {started ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 md:px-5 pt-4">
          <div className="inline-flex bg-line rounded-full p-[3px] gap-0.5">
            {(["pipeline", "editor"] as const).map((v) => (
              <button
                key={v}
                onClick={() => onView(v)}
                disabled={v === "editor" && !rendered}
                className={`px-4 py-1 rounded-full text-xs font-semibold capitalize ${
                  stage === v ? "bg-panel text-ink shadow-sm" : v === "editor" && !rendered ? "text-faint" : "text-muted"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          {!rendered ? <span className="hidden sm:inline text-xs text-muted">the editor opens when the first render lands</span> : null}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[12.5px] text-muted tabular-nums">
              ${project.spentUsd} spent{project.budgetUsd ? ` of $${project.budgetUsd} cap` : ""}
            </span>
            {rendered ? (
              <>
                <Action icon="download" label="Download" href={((u) => (u ? `${u}?download=1` : undefined))(project.renders.find((r) => r.output)?.output?.url)} />
                {/* One button: now or later is asked in its window (2026-09-29, the owner). */}
                <Action icon="send" label="Post" onClick={() => setPublishing(true)} />
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {publishing ? <PublishDialog piece={publishableOf(project)} onClose={() => setPublishing(false)} /> : null}

      <div className="flex-1 min-h-0">
        {stage === "photos" ? <Photos project={project} onDone={onPicked} /> : null}
        {stage === "voices" ? <Voices project={project} /> : null}
        {stage === "plan" ? <Plan project={project} /> : null}
        {stage === "pipeline" ? <Pipeline project={project} /> : null}
        {stage === "editor" ? <Editor project={project} /> : null}
      </div>
    </div>
  );
}

/** Publishing always opens the dialog and always ends in a human pressing it (ADR 0011). */
function Action({ icon, label, href, onClick }: { icon: string; label: string; href?: string | undefined; onClick?: () => void }) {
  const cls = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page";
  if (href) return <a href={href} download className={cls}><Icon name={icon} size={14} /> {label}</a>;
  if (onClick) return <button onClick={onClick} className={cls}><Icon name={icon} size={14} /> {label}</button>;
  return <button className={`${cls} opacity-50`} disabled><Icon name={icon} size={14} /> {label}</button>;
}
